import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { translateText } from '../services/translate';
import { useNavigate } from 'react-router-dom';
import '../styles/AdminMembers.css';
import '../styles/Partners.css';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/members/`;

const initialFormData = {
  prenom: '',
  nom: '',
  role: '', role_en: '', role_ar: '',
  date_naissance: '',
  bio: '', bio_en: '', bio_ar: '',
  annees_engagement: 0,
  photo: null,
};

function AdminMembers({ onStatsChange }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const [membres, setMembres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMembre, setEditingMembre] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState({ show: false, title: '', message: '', type: 'warning', onConfirm: null });
  const [successModal, setSuccessModal] = useState({ show: false, title: '', message: '' });
  const [viewMode, setViewMode] = useState('grid');
  const isSuperAdmin = localStorage.getItem('is_superadmin') === 'true';
  const canManage = isSuperAdmin || localStorage.getItem('can_manage_members') === 'true';

  const getToken = () => localStorage.getItem('access_token');

  const authHeaders = () => ({
    Authorization: `Bearer ${getToken()}`,
  });

  const handleUnauthorized = (response) => {
    if (response.status === 401 || response.status === 403) {
      navigate('/admin/login');
      return true;
    }
    return false;
  };

  const closeModal = () => setModal(m => ({ ...m, show: false, onConfirm: null }));
  const confirmModal = (title, message, onConfirm) =>
    setModal({ show: true, type: 'warning', title, message, onConfirm });

  // ── Fetch list ────────────────────────────────────────────────────────────

  const fetchMembres = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL, {
        headers: {
          ...authHeaders(),
          'Accept-Language': i18n.language || 'fr'
        },
      });
      if (handleUnauthorized(response)) return;
      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }
      const data = await response.json();
      setMembres(data);
    } catch (err) {
      setError('Erreur lors du chargement des membres.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // ── Form helpers ──────────────────────────────────────────────────────────

  const openCreateForm = () => {
    setEditingMembre(null);
    setFormData(initialFormData);
    setFormErrors({});
    setShowForm(true);
  };

  const openEditForm = (membre) => {
    setEditingMembre(membre);
    setFormData({
      prenom: membre.prenom || '',
      nom: membre.nom || '',
      role: membre.role || '',
      role_en: membre.role_en || '',
      role_ar: membre.role_ar || '',
      date_naissance: membre.date_naissance || '',
      bio: membre.bio || '',
      bio_en: membre.bio_en || '',
      bio_ar: membre.bio_ar || '',
      annees_engagement: membre.annees_engagement ?? 0,
      photo: null,
    });
    setFormErrors({});
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingMembre(null);
    setFormData(initialFormData);
    setFormErrors({});
  };

  const handleFieldChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'photo') {
      setFormData((prev) => ({ ...prev, photo: files[0] || null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleAutoTranslate = async () => {
    if (!formData.role && !formData.bio) {
      setSuccessModal({ show: true, message: 'Veuillez remplir au moins le rôle ou la biographie en français.', title: 'Champs vides' });
      return;
    }

    setSubmitting(true);
    try {
      const translations = {};

      if (formData.role) {
        translations.role_en = await translateText(formData.role, 'fr', 'en');
        translations.role_ar = await translateText(formData.role, 'fr', 'ar');
      }

      if (formData.bio) {
        translations.bio_en = await translateText(formData.bio, 'fr', 'en');
        translations.bio_ar = await translateText(formData.bio, 'fr', 'ar');
      }

      setFormData(prev => ({ ...prev, ...translations }));
      setSuccessModal({ show: true, message: 'Le rôle et la biographie ont été traduits.', title: 'Traduction terminée' });
    } catch {
      setSuccessModal({ show: true, message: 'La traduction a échoué.', title: 'Erreur' });
    } finally {
      setSubmitting(false);
    }
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('prenom', formData.prenom);
    fd.append('nom', formData.nom);
    fd.append('role', formData.role);
    fd.append('role_en', formData.role_en);
    fd.append('role_ar', formData.role_ar);
    if (formData.date_naissance) fd.append('date_naissance', formData.date_naissance);
    if (formData.bio) fd.append('bio', formData.bio);
    fd.append('bio_en', formData.bio_en);
    fd.append('bio_ar', formData.bio_ar);
    fd.append('annees_engagement', formData.annees_engagement);
    if (formData.photo) fd.append('photo', formData.photo);
    return fd;
  };

  const handleCreate = async () => {
    setSubmitting(true);
    setFormErrors({});
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: authHeaders(),
        body: buildFormData(),
      });
      if (handleUnauthorized(response)) return;
      if (response.status === 201) {
        closeForm();
        fetchMembres();
        if (onStatsChange) onStatsChange();
        setSuccessModal({ show: true, message: 'Le nouveau membre a été enregistré.', title: 'Membre ajouté' });
      } else if (response.status === 400) {
        const errors = await response.json();
        setFormErrors(errors);
      } else {
        setFormErrors({ non_field_errors: [`Erreur inattendue (${response.status})`] });
      }
    } catch (err) {
      setFormErrors({ non_field_errors: ['Erreur réseau. Veuillez réessayer.'] });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id) => {
    setSubmitting(true);
    setFormErrors({});
    try {
      const response = await fetch(`${API_URL}${id}/`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: buildFormData(),
      });
      if (handleUnauthorized(response)) return;
      if (response.status === 200) {
        const updated = await response.json();
        setMembres((prev) =>
          prev.map((m) => (m.id === id ? updated : m))
        );
        closeForm();
      } else if (response.status === 400) {
        const errors = await response.json();
        setFormErrors(errors);
      } else {
        setFormErrors({ non_field_errors: [`Erreur inattendue (${response.status})`] });
      }
    } catch (err) {
      setFormErrors({ non_field_errors: ['Erreur réseau. Veuillez réessayer.'] });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    confirmModal(
      'Supprimer le membre',
      'Ce membre sera définitivement supprimé. Confirmer ?',
      async () => {
        try {
          const response = await fetch(`${API_URL}${id}/`, {
            method: 'DELETE',
            headers: authHeaders(),
          });
          if (handleUnauthorized(response)) return;
          if (response.status === 204) {
            setMembres((prev) => prev.filter((m) => m.id !== id));
            if (onStatsChange) onStatsChange();
            setSuccessModal({ show: true, title: 'Supprimé', message: 'Le membre a été supprimé.' });
          }
        } catch (err) {
          console.error('Erreur lors de la suppression :', err);
        }
      }
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingMembre) {
      handleUpdate(editingMembre.id);
    } else {
      handleCreate();
    }
  };

  return (
    <section className="membres-section">
      {modal.show && (
        <div className="tiwizi-modal-overlay" onClick={closeModal}>
          <div className="tiwizi-modal-card tiwizi-details-modal" onClick={e => e.stopPropagation()}>
            <div className="tiwizi-modal-icon warning">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3>{modal.title}</h3>
            <p>{modal.message}</p>
            <div className="tiwizi-modal-actions">
              <button className="tiwizi-btn-cancel" onClick={closeModal}>Annuler</button>
              <button className="tiwizi-btn-confirm" onClick={() => { modal.onConfirm(); closeModal(); }}>Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {successModal.show && (
        <div className="tiwizi-modal-overlay" onClick={() => setSuccessModal({ show: false, title: '', message: '' })}>
          <div className="tiwizi-modal-card tiwizi-details-modal" onClick={e => e.stopPropagation()} style={{ textAlign: 'center', maxWidth: '420px' }}>
            <div className="tiwizi-modal-icon success" style={{ margin: '0 auto 16px' }}>
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 style={{ marginBottom: '8px' }}>{successModal.title}</h3>
            <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '12px 0' }} />
            <p style={{ color: '#666', marginBottom: '20px' }}>{successModal.message}</p>
            <button
              onClick={() => setSuccessModal({ show: false, title: '', message: '' })}
              style={{
                background: '#1a1a1a',
                color: 'white',
                border: 'none',
                borderRadius: '30px',
                padding: '12px 40px',
                fontWeight: '700',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      )}
      <div className="membres-header">
        <h3>Membres de l'association</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="projets-view-toggle">
            <button type="button" className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Vue Liste">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
            <button type="button" className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Vue Grille">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
              </svg>
            </button>
          </div>
          {canManage && (
            <button className="btn-add-membre" onClick={openCreateForm}>
              + Ajouter un membre
            </button>
          )}
        </div>
      </div>

      {loading && <p className="membres-loading">Chargement des données...</p>}

      {!loading && error && <p className="membres-error">{error}</p>}

      {!loading && !error && (
        <>
          {membres.length === 0 ? (
            <p className="membres-empty">Aucun membre pour le moment.</p>
          ) : viewMode === 'grid' ? (
            <div className="membres-cards-grid">
              {membres.map((membre) => (
                <div key={membre.id} className="membre-card">
                  <div className="membre-card-photo-wrapper">
                    {membre.photo ? (
                      <img
                        src={membre.photo}
                        alt={`${membre.prenom} ${membre.nom}`}
                        className="membre-card-photo"
                      />
                    ) : (
                      <div className="membre-card-avatar">
                        {membre.prenom?.[0]}{membre.nom?.[0]}
                      </div>
                    )}
                  </div>

                  <div className="membre-card-info">
                    <h4 className="membre-card-nom">{membre.prenom} {membre.nom}</h4>
                    <span className="membre-card-role">{membre.role}</span>
                    {membre.date_naissance && (
                      <span className="membre-card-date">{membre.date_naissance}</span>
                    )}
                    {membre.annees_engagement > 0 && (
                      <span className="membre-card-engagement">{membre.annees_engagement} ans d'engagement</span>
                    )}
                  </div>

                  {canManage && (
                    <div className="membre-card-actions">
                      <button
                        className="membre-card-btn edit"
                        onClick={() => openEditForm(membre)}
                      >
                        Modifier
                      </button>
                      <button
                        className="membre-card-btn delete"
                        onClick={() => handleDelete(membre.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Photo</th>
                    <th>Nom</th>
                    <th>Rôle</th>
                    <th>Engagement</th>
                    <th>Date naissance</th>
                    {canManage && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {membres.map(m => (
                    <tr key={m.id}>
                      <td>
                        {m.photo
                          ? <img src={m.photo} alt={`${m.prenom} ${m.nom}`} className="table-logo-preview" style={{ borderRadius: '50%' }} />
                          : <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#f5c50a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.85rem', color: '#1a1a1a' }}>{m.prenom?.[0]}{m.nom?.[0]}</div>
                        }
                      </td>
                      <td><strong>{m.prenom} {m.nom}</strong></td>
                      <td>{m.role}</td>
                      <td>{m.annees_engagement > 0 ? `${m.annees_engagement} ans` : '—'}</td>
                      <td>{m.date_naissance || '—'}</td>
                      {canManage && (
                        <td>
                          <div className="action-btns">
                            <button className="icon-btn" title="Modifier" onClick={() => openEditForm(m)}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                            </button>
                            <button className="icon-btn" title="Supprimer" style={{ color: '#e74c3c' }} onClick={() => handleDelete(m.id)}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeForm(); }}>
          <div className="modal-card">
            <div className="modal-header">
              <h3>{editingMembre ? 'Modifier le membre' : 'Ajouter un membre'}</h3>
              <button className="modal-close-btn" onClick={closeForm} title="Fermer">✕</button>
            </div>

            <form className="membre-form" onSubmit={handleSubmit}>
              {formErrors.non_field_errors && (
                <p className="field-error">{formErrors.non_field_errors.join(' ')}</p>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Prénom <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleFieldChange}
                    className={formErrors.prenom ? 'error' : ''}
                    placeholder="Fatima"
                    required
                  />
                  {formErrors.prenom && (
                    <span className="field-error">{formErrors.prenom.join(' ')}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Nom <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleFieldChange}
                    className={formErrors.nom ? 'error' : ''}
                    placeholder="Benali"
                    required
                  />
                  {formErrors.nom && (
                    <span className="field-error">{formErrors.nom.join(' ')}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Rôle (FR) <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleFieldChange}
                    className={formErrors.role ? 'error' : ''}
                    placeholder="Présidente"
                    required
                  />
                  {formErrors.role && (
                    <span className="field-error">{formErrors.role.join(' ')}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Rôle (EN)</label>
                  <input
                    type="text"
                    name="role_en"
                    value={formData.role_en}
                    onChange={handleFieldChange}
                    placeholder="President"
                  />
                </div>

                <div className="form-group">
                  <label>Rôle (AR)</label>
                  <input
                    type="text"
                    name="role_ar"
                    value={formData.role_ar}
                    onChange={handleFieldChange}
                    placeholder="..."
                    style={{ direction: 'rtl' }}
                  />
                </div>

                <div className="form-group">
                  <label>Photo</label>
                  <input
                    type="file"
                    name="photo"
                    accept="image/*"
                    onChange={handleFieldChange}
                  />
                  {formErrors.photo && (
                    <span className="field-error">{formErrors.photo.join(' ')}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date de naissance</label>
                  <input
                    type="date"
                    name="date_naissance"
                    value={formData.date_naissance}
                    onChange={handleFieldChange}
                    className={formErrors.date_naissance ? 'error' : ''}
                  />
                  {formErrors.date_naissance && (
                    <span className="field-error">{formErrors.date_naissance.join(' ')}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Ans d'engagement</label>
                  <input
                    type="number"
                    name="annees_engagement"
                    value={formData.annees_engagement}
                    onChange={handleFieldChange}
                    min="0"
                    placeholder="0"
                  />
                  {formErrors.annees_engagement && (
                    <span className="field-error">{formErrors.annees_engagement.join(' ')}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Biographie (FR)</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleFieldChange}
                  className={formErrors.bio ? 'error' : ''}
                  placeholder="Quelques mots sur ce membre..."
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Biographie (EN)</label>
                <textarea
                  name="bio_en"
                  value={formData.bio_en}
                  onChange={handleFieldChange}
                  placeholder="A few words..."
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Biographie (AR)</label>
                <textarea
                  name="bio_ar"
                  value={formData.bio_ar}
                  onChange={handleFieldChange}
                  placeholder="..."
                  rows={2}
                  style={{ direction: 'rtl' }}
                />
              </div>

              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #eee' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#666' }}>Outils de traduction</span>
                  <button
                    type="button"
                    onClick={handleAutoTranslate}
                    className="btn-translate-auto"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#f5c50a',
                      color: '#1a1a1a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem'
                    }}
                    disabled={submitting}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" />
                    </svg>
                    {submitting ? 'Traduction...' : 'Traduire automatiquement'}
                  </button>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#888', margin: 0 }}>
                  Remplit automatiquement le rôle et la biographie en Anglais et Arabe.
                </p>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={closeForm}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting
                    ? 'Enregistrement...'
                    : editingMembre
                      ? 'Enregistrer les modifications'
                      : 'Créer le membre'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

export default AdminMembers;
